import { useEffect, useState, useRef } from "react";
import { api, fmtCurrency, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Upload, FileText, Check, X, RefreshCcw, Trash2, Mail, Bot, Loader2 } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABELS = {
  incoming_invoice: "Eingangsrechnung",
  outgoing_invoice: "Ausgangsrechnung",
  offer: "Angebot",
  credit_note: "Gutschrift",
  reminder: "Mahnung",
  contract: "Vertrag",
  bank_statement: "Kontoauszug",
  other: "Sonstiges",
};
const TYPE_COLORS = {
  incoming_invoice: "bg-amber-100 text-amber-700",
  outgoing_invoice: "bg-blue-100 text-blue-700",
  offer: "bg-violet-100 text-violet-700",
  credit_note: "bg-cyan-100 text-cyan-700",
  reminder: "bg-red-100 text-red-700",
  contract: "bg-emerald-100 text-emerald-700",
  bank_statement: "bg-neutral-100 text-neutral-700",
  other: "bg-neutral-100 text-neutral-700",
};

export default function InboxPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(null);
  const fileRef = useRef(null);

  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [imapAccounts, setImapAccounts] = useState([]);

  const load = async () => {
    const [inb, cu, pr, ima] = await Promise.all([
      api.get("/inbox"), api.get("/customers"), api.get("/projects"), api.get("/imap/accounts"),
    ]);
    setItems(inb.data); setCustomers(cu.data); setProjects(pr.data); setImapAccounts(ima.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("purpose", "inbox");
        const { data } = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (data.duplicate) toast.info(`${f.name}: bereits vorhanden (Duplikat)`);
        else toast.success(`${f.name}: hochgeladen + analysiert`);
      } catch (err) {
        toast.error(`${f.name}: ${err.response?.data?.detail || err.message}`);
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
    load();
  };

  const reextract = async (id) => {
    toast.message("GPT-5.2 analysiert erneut…");
    try {
      await api.post(`/inbox/${id}/reextract`);
      toast.success("Neu extrahiert");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const del = async (id) => {
    if (!window.confirm("Dokument wirklich entfernen?")) return;
    await api.delete(`/inbox/${id}`);
    toast.success("Entfernt");
    load();
  };

  const syncAccount = async (accId) => {
    toast.message("IMAP-Synchronisation läuft…");
    try {
      const { data } = await api.post(`/imap/accounts/${accId}/sync`);
      toast.success(`${data.new_items} neue Belege aus ${data.messages_seen} Mails`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "IMAP-Fehler"); }
  };

  const pending = items.filter(i => i.status === "pending");

  return (
    <div className="space-y-6" data-testid="inbox-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">Belegeingang</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Inbox „Zur Prüfung&ldquo;</h1>
          <p className="text-sm text-neutral-500 mt-2">Lade PDFs hoch oder lasse IMAP automatisch Belege abholen. GPT-5.2 erkennt Lieferant, Beträge und Projektbezug.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {imapAccounts.filter(a => a.active).map(a => (
            <Button key={a.id} variant="outline" onClick={() => syncAccount(a.id)} data-testid={`imap-sync-${a.id}`}>
              <Mail className="h-4 w-4 mr-2" /> {a.label} synchronisieren
            </Button>
          ))}
          <input ref={fileRef} type="file" multiple accept=".pdf,.png,.jpg,.jpeg" hidden onChange={onUpload} data-testid="inbox-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="inbox-upload-btn">
            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            PDF hochladen
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Stat label="Zur Prüfung" value={pending.length} />
        <Stat label="Akzeptiert" value={items.filter(i => i.status === "accepted").length} />
        <Stat label="Abgelehnt" value={items.filter(i => i.status === "rejected").length} />
        <Stat label="IMAP-Konten" value={imapAccounts.length} />
      </div>

      {loading ? <div className="text-neutral-500">Lade…</div> : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-neutral-300 bg-white p-12 text-center" data-testid="inbox-empty">
          <FileText className="h-10 w-10 mx-auto text-neutral-400 mb-3" />
          <div className="font-display text-lg font-semibold mb-1">Noch keine Belege</div>
          <p className="text-sm text-neutral-500">Lade dein erstes PDF hoch oder richte ein IMAP-Konto ein.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="inbox-list">
          {items.map((it) => {
            const ex = it.extracted || {};
            const type = it.suggested_type || "other";
            return (
              <div key={it.id} className={`rounded-xl border bg-white p-5 ${it.status === "pending" ? "border-neutral-200" : "border-neutral-100 opacity-70"}`} data-testid={`inbox-item-${it.id}`}>
                <div className="flex items-start gap-4">
                  <div className="h-11 w-11 rounded-lg bg-neutral-100 flex items-center justify-center shrink-0">
                    <FileText className="h-5 w-5 text-neutral-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Badge className={TYPE_COLORS[type]} variant="secondary">{TYPE_LABELS[type]}</Badge>
                      {it.source === "imap" && <Badge variant="outline" className="text-[10px]"><Mail className="h-3 w-3 mr-1" />IMAP</Badge>}
                      {it.confidence > 0 && <span className="text-[11px] text-neutral-500"><Bot className="h-3 w-3 inline mr-0.5" />Konfidenz {Math.round(it.confidence * 100)}%</span>}
                      {it.status === "accepted" && <Badge className="bg-emerald-100 text-emerald-700" variant="secondary">Akzeptiert</Badge>}
                      {it.status === "rejected" && <Badge className="bg-red-100 text-red-700" variant="secondary">Abgelehnt</Badge>}
                    </div>
                    <div className="font-display font-semibold text-neutral-900 truncate">
                      {ex.supplier_name || ex.customer_name || it.original_filename}
                    </div>
                    <div className="text-sm text-neutral-500 mt-0.5 flex gap-4 flex-wrap">
                      {ex.invoice_number && <span>Nr. {ex.invoice_number}</span>}
                      {ex.invoice_date && <span>📅 {fmtDate(ex.invoice_date)}</span>}
                      {ex.due_date && <span>Fällig {fmtDate(ex.due_date)}</span>}
                      {ex.gross_amount != null && <span className="font-medium text-neutral-700">{fmtCurrency(ex.gross_amount, ex.currency || "EUR")}</span>}
                      {it.email_from && <span className="text-[11px] truncate">von {it.email_from}</span>}
                    </div>
                    {ex.project_hint && <div className="text-xs text-neutral-500 mt-1">Projekthinweis: {ex.project_hint}</div>}
                    {it.error && <div className="text-xs text-red-600 mt-2">⚠ {it.error}</div>}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {it.status === "pending" && (
                      <>
                        <Button size="sm" onClick={() => setSelected(it)} data-testid={`inbox-process-${it.id}`}><Check className="h-4 w-4 mr-1" />Verarbeiten</Button>
                        <Button size="sm" variant="ghost" onClick={() => reextract(it.id)} title="Erneut analysieren" data-testid={`inbox-reextract-${it.id}`}><RefreshCcw className="h-4 w-4" /></Button>
                      </>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => del(it.id)} data-testid={`inbox-delete-${it.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ProcessDialog
        item={selected}
        onClose={() => { setSelected(null); load(); }}
        customers={customers}
        projects={projects}
      />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-5">
      <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">{label}</div>
      <div className="font-display text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}

function ProcessDialog({ item, onClose, customers, projects }) {
  const ex = item?.extracted || {};
  const initialTarget = ex.document_type === "outgoing_invoice" ? "outgoing_invoice" : ex.document_type === "incoming_invoice" ? "incoming_invoice" : "incoming_invoice";
  const [target, setTarget] = useState(initialTarget);
  const [projectId, setProjectId] = useState(item?.suggested_project_id || "");
  const [customerId, setCustomerId] = useState(item?.suggested_customer_id || "");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (item) {
      const t = item.extracted?.document_type === "outgoing_invoice" ? "outgoing_invoice" : item.extracted?.document_type === "incoming_invoice" ? "incoming_invoice" : "incoming_invoice";
      setTarget(t);
      setProjectId(item.suggested_project_id || "");
      setCustomerId(item.suggested_customer_id || "");
    }
  }, [item]);

  if (!item) return null;

  const submit = async () => {
    setLoading(true);
    try {
      await api.post(`/inbox/${item.id}/accept`, {
        target,
        project_id: projectId || null,
        customer_id: customerId || null,
      });
      toast.success("Beleg verarbeitet ✓");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
    setLoading(false);
  };

  const reject = async () => {
    await api.post(`/inbox/${item.id}/accept`, { target: "reject" });
    toast.message("Beleg abgelehnt");
    onClose();
  };

  return (
    <Dialog open={!!item} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-xl" data-testid="inbox-process-dialog">
        <DialogHeader><DialogTitle>Beleg verarbeiten</DialogTitle><DialogDescription>Wähle, als welcher Beleg dieses Dokument importiert werden soll.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg bg-neutral-50 border border-neutral-200 p-3 text-sm">
            <div className="font-semibold">{ex.supplier_name || ex.customer_name || item.original_filename}</div>
            <div className="text-neutral-600 mt-1 text-xs">
              {ex.invoice_number} · {fmtDate(ex.invoice_date)} · <span className="font-medium">{fmtCurrency(ex.gross_amount, ex.currency || "EUR")}</span>
            </div>
            {ex.line_items?.length > 0 && (
              <ul className="mt-2 text-xs text-neutral-600">
                {ex.line_items.slice(0, 3).map((li, i) => <li key={i}>· {li.description} — {fmtCurrency(li.total, ex.currency || "EUR")}</li>)}
              </ul>
            )}
          </div>

          <div>
            <Label>Als was importieren?</Label>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger data-testid="inbox-target"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="incoming_invoice">Eingangsrechnung (Lieferant/Freelancer)</SelectItem>
                <SelectItem value="outgoing_invoice">Ausgangsrechnung (Kunde)</SelectItem>
                <SelectItem value="fixed_cost">Fixkosten (monatlich)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {target === "outgoing_invoice" && (
            <div>
              <Label>Kunde</Label>
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger data-testid="inbox-customer-select"><SelectValue placeholder="Kunde wählen" /></SelectTrigger>
                <SelectContent>
                  {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {(target === "incoming_invoice" || target === "outgoing_invoice") && (
            <div>
              <Label>Projektbezug (optional)</Label>
              <Select value={projectId || "none"} onValueChange={(v) => setProjectId(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="inbox-project-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Kein Projekt —</SelectItem>
                  {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={reject} data-testid="inbox-reject"><X className="h-4 w-4 mr-1" />Ablehnen</Button>
          <Button onClick={submit} disabled={loading} data-testid="inbox-accept">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
            Importieren
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
